import { ReactNode, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';

import { GachaRecoveryMode } from '../../../../../../__gqltypes__';
import { describeGachaMatcherFilters, GachaMatcherDraft, GachaMysteryDraft, newGachaMysteryDraft } from '../utils';
import { validateGachaMystery } from '../validation';
import { GachaDurationInput } from './GachaDurationInput';
import { describeGachaMatcher, GachaMatcherFields, GachaMatcherSection } from './GachaMatcherFields';
import { WeightedRewardTable } from './WeightedRewardTable';

type Props = {
  boxClientId: string;
  mystery: GachaMysteryDraft | null;
  // The campaign's CURRENT (edited) matcher, shown read-only under "Same as campaign".
  campaignMatcher: GachaMatcherDraft;
  onChange: (mystery: GachaMysteryDraft | null) => void;
  // Template controls for the mystery pool table.
  poolHeaderExtra?: ReactNode;
  // A campaign created in this session: its matcher sections start expanded.
  isNewCampaign?: boolean;
};

const ONE_DAY_SECONDS = 86400;
const PREVIEW_PULLS = 8;

const toNumber = (raw: string): number => {
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

// The rate of each pull, as the server decays it: max(minimum, first - decrease x pulls).
export function gachaMysteryRatePreview(mystery: GachaMysteryDraft, pulls = PREVIEW_PULLS): number[] {
  return Array.from({ length: pulls }, (_, pull) => {
    const decayed = mystery.firstRatePercent - mystery.decreasePercent * pull;
    return Math.min(100, Math.max(0, Math.max(mystery.minRatePercent, decayed)));
  });
}

/**
 * The box's mystery reward (spec §8): an extra reward with its own matcher ("Same as campaign" by default, a real
 * inheritance), a decaying chance counted per claim, and a global recovery timer anchored at the campaign start.
 */
export function GachaMysterySection({
  boxClientId,
  mystery,
  campaignMatcher,
  onChange,
  poolHeaderExtra,
  isNewCampaign = false,
}: Props): JSX.Element {
  const idPrefix = `gacha-mystery-${boxClientId}`;
  const enabled = mystery?.enabled === true;
  const update = (patch: Partial<GachaMysteryDraft>) => mystery && onChange({ ...mystery, ...patch });
  const errors = mystery && enabled ? validateGachaMystery(mystery, 'Mystery reward') : [];
  // Pool errors are listed under the pool table itself.
  const sectionErrors = errors.filter((error) => !error.startsWith('Mystery reward reward table'));
  // Collapsed for a saved mystery; expanded on a new campaign and on a mystery added in this edit.
  const [matcherExpanded] = useState(() => isNewCampaign || mystery == null);
  const eligibilityFilters = mystery
    ? describeGachaMatcherFilters(mystery.inherit ? campaignMatcher : mystery.criteria)
    : [];

  return (
    <Card className="mb-3 border-dark">
      <Card.Header>
        <Form.Check
          type="switch"
          id={`${idPrefix}-enabled`}
          label={<strong>Mystery reward</strong>}
          checked={enabled}
          onChange={(e) => {
            if (!mystery) {
              onChange(newGachaMysteryDraft());
            } else {
              onChange({ ...mystery, enabled: e.target.checked });
            }
          }}
        />
        <div className="small text-muted">
          One extra reward per opening, on top of the box contents. Basic players watch a rewarded ad to claim it, Plus
          players claim it directly (owning Maestro does not remove the ad). No price.
        </div>
      </Card.Header>
      {mystery && enabled && (
        <Card.Body>
          <GachaMatcherSection
            title={mystery.inherit ? 'Who is eligible' : 'Who is eligible (own criteria)'}
            filters={eligibilityFilters}
            defaultExpanded={matcherExpanded}
            inherited={mystery.inherit}
          >
            <Form.Group as={Row} className="mb-2">
              <Col md={{ span: 9, offset: 3 }}>
                <Form.Check
                  type="radio"
                  id={`${idPrefix}-inherit`}
                  name={`${idPrefix}-matcher`}
                  label="Same as campaign"
                  checked={mystery.inherit}
                  onChange={() => update({ inherit: true })}
                />
                <Form.Check
                  type="radio"
                  id={`${idPrefix}-own`}
                  name={`${idPrefix}-matcher`}
                  label="Own criteria"
                  checked={!mystery.inherit}
                  onChange={() => update({ inherit: false })}
                />
              </Col>
            </Form.Group>
            {mystery.inherit ? (
              <div className="border rounded p-2 mb-2 bg-light">
                <div className="small text-muted mb-1">
                  Follows the campaign&apos;s matcher, including later edits to it. Currently:{' '}
                  <strong>{describeGachaMatcher(campaignMatcher)}</strong>
                </div>
                <GachaMatcherFields idPrefix={`${idPrefix}-inherited`} matcher={campaignMatcher} readOnly />
              </div>
            ) : (
              <GachaMatcherFields
                idPrefix={`${idPrefix}-criteria`}
                matcher={mystery.criteria}
                onChange={(criteria) => update({ criteria })}
              />
            )}
          </GachaMatcherSection>

          <h6 className="text-muted text-uppercase small mt-3">Chance</h6>
          <Form.Group as={Row} className="mb-2">
            <Col md={3}>
              <Form.Label className="small mb-0" htmlFor={`${idPrefix}-first`}>
                First drop rate %
              </Form.Label>
              <Form.Control
                id={`${idPrefix}-first`}
                type="number"
                min={0}
                max={100}
                step="any"
                value={mystery.firstRatePercent}
                onChange={(e) => update({ firstRatePercent: toNumber(e.target.value) })}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-0" htmlFor={`${idPrefix}-decrease`}>
                Decrease per pull %
              </Form.Label>
              <Form.Control
                id={`${idPrefix}-decrease`}
                type="number"
                min={0}
                max={100}
                step="any"
                value={mystery.decreasePercent}
                onChange={(e) => update({ decreasePercent: toNumber(e.target.value) })}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-0" htmlFor={`${idPrefix}-min`}>
                Minimum drop rate %
              </Form.Label>
              <Form.Control
                id={`${idPrefix}-min`}
                type="number"
                min={0}
                max={100}
                step="any"
                value={mystery.minRatePercent}
                onChange={(e) => update({ minRatePercent: toNumber(e.target.value) })}
              />
            </Col>
          </Form.Group>
          <Form.Text className="text-muted mb-2">
            A pull counts when the player claims the mystery reward, per player per box. Rates of the first{' '}
            {PREVIEW_PULLS} pulls:{' '}
            {gachaMysteryRatePreview(mystery)
              .map((rate) => `${rate}%`)
              .join(', ')}
            …
          </Form.Text>

          <h6 className="text-muted text-uppercase small mt-3">Recovery</h6>
          <Form.Group as={Row} className="mb-2">
            <Col md={{ span: 3, offset: 3 }} className="pt-2">
              <Form.Check
                type="radio"
                id={`${idPrefix}-recovery-none`}
                name={`${idPrefix}-recovery`}
                label="No refresh"
                checked={mystery.recoveryMode === GachaRecoveryMode.NONE}
                onChange={() => update({ recoveryMode: GachaRecoveryMode.NONE })}
              />
              <Form.Check
                type="radio"
                id={`${idPrefix}-recovery-global`}
                name={`${idPrefix}-recovery`}
                label="Global timer"
                checked={mystery.recoveryMode === GachaRecoveryMode.GLOBAL}
                onChange={() =>
                  update({
                    recoveryMode: GachaRecoveryMode.GLOBAL,
                    recoveryIntervalSeconds: mystery.recoveryIntervalSeconds ?? ONE_DAY_SECONDS,
                  })
                }
              />
            </Col>
            {mystery.recoveryMode === GachaRecoveryMode.GLOBAL && (
              <Col md={4}>
                <GachaDurationInput
                  id={`${idPrefix}-recovery-interval`}
                  value={mystery.recoveryIntervalSeconds}
                  onChange={(recoveryIntervalSeconds) => update({ recoveryIntervalSeconds })}
                />
              </Col>
            )}
          </Form.Group>
          <Form.Text className="text-muted mb-3">
            {mystery.recoveryMode === GachaRecoveryMode.GLOBAL
              ? 'Every player goes back to the first rate at the same moments: a repeating interval counted from ' +
                'the campaign start (UTC). Moving the campaign start moves these moments.'
              : 'Once at the minimum the chance stays there for the rest of the campaign.'}
          </Form.Text>

          {sectionErrors.length > 0 && (
            <ul className="small text-danger">
              {sectionErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          )}

          <WeightedRewardTable
            variant="mystery"
            tableId={`gacha-mystery-pool-${boxClientId}`}
            title="Mystery reward table"
            hint="One reward is rolled from this table when the mystery reward drops."
            rows={mystery.pool}
            onChange={(pool) => update({ pool })}
            headerExtra={poolHeaderExtra}
          />
        </Card.Body>
      )}
    </Card>
  );
}
